/* =============================================================================
   assets/rm-referral-tracking.js

   Quiz-forward referral tracking.

   Problem: Jane takes the Engagement Ring Quiz, gets emailed her results.
   She forwards that email to Jeff, who clicks through and buys. Klaviyo's
   own tracking stops at Jane's profile — it never learns Jeff converted
   off the back of Jane's quiz.

   Fix: Email #1's links carry two params:
     - klaviyo_ref        = {{ person.id }}  (dynamic — Jane's Klaviyo profile ID)
     - klaviyo_ref_source = a static string baked into that email template,
                            e.g. "engagement_quiz" or "mens_band_quiz" — same
                            value for every recipient of that email, since it
                            identifies the CAMPAIGN, not the person.

   Whoever clicks that link — Jane or Jeff — this script:
     1. Reads both params off the URL on any page of the site and stores
        them (localStorage), so they survive across pages during the
        browsing session and for MAX_AGE_DAYS afterward.
     2. Writes both values onto the Shopify cart as cart attributes
        (`attributes[klaviyo_ref]`, `attributes[klaviyo_ref_source]`) via
        /cart/update.js. Cart attributes ride along into checkout
        automatically and land on the completed order as note_attributes —
        this is what makes them survive into the order, regardless of
        cookie/localStorage domain quirks on checkout.
     3. The Zapier "Placed Order" pipeline can then read
        note_attributes.klaviyo_ref and note_attributes.klaviyo_ref_source
        off the order and write them to the purchaser's Klaviyo profile as
        `referred_by_profile_id` and `referred_by_source`, closing the loop
        back to Jane and telling you which campaign drove the referral.

   Named `klaviyo_ref` / `klaviyo_ref_source` (not just "ref") on purpose —
   plain "ref" collides with other tools that use the same param (affiliate
   apps, ad platforms), and these names are self-explanatory later in the
   Shopify admin order notes or Zapier without needing this comment to know
   what they mean.

   klaviyo_ref_source is optional — if a link only carries klaviyo_ref (no
   source), that's fine; the source attribute just won't be set/updated.

   MAX_AGE_DAYS default: 60 days. Rustic & Main's engagement sales cycle
   runs 6-8 weeks (42-56 days), so this is set a little longer than that
   window on purpose. Adjust the constant below if you want it shorter/longer.
   ============================================================================= */

(function () {
  'use strict';

  /* ── Config ── */
  var REF_PARAM          = 'klaviyo_ref';
  var REF_SOURCE_PARAM   = 'klaviyo_ref_source';

  var STORAGE_KEY_ID      = 'rm_klaviyo_ref_id';
  var STORAGE_KEY_SOURCE  = 'rm_klaviyo_ref_source';
  var STORAGE_KEY_TS      = 'rm_klaviyo_ref_ts';
  var SESSION_SYNC_KEY    = 'rm_klaviyo_ref_synced'; // avoids re-hitting /cart.js + /cart/update.js on every pageview once synced

  var MAX_AGE_DAYS        = 60;
  var MAX_AGE_MS          = MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

  /* ── Step 1: capture ?klaviyo_ref= (+ optional ?klaviyo_ref_source=) off the URL, persist (last-touch) ── */
  function captureRefFromUrl() {
    try {
      var params = new URLSearchParams(window.location.search);
      var refFromUrl = params.get(REF_PARAM);
      if (refFromUrl) {
        localStorage.setItem(STORAGE_KEY_ID, refFromUrl);
        localStorage.setItem(STORAGE_KEY_TS, Date.now().toString());

        var sourceFromUrl = params.get(REF_SOURCE_PARAM);
        if (sourceFromUrl) {
          localStorage.setItem(STORAGE_KEY_SOURCE, sourceFromUrl);
        } else {
          // New ref with no source on this link — clear any stale source
          // from a previous referral so we don't misattribute it.
          localStorage.removeItem(STORAGE_KEY_SOURCE);
        }

        // A fresh ref on the URL means this is a new referral click —
        // force a re-sync to the cart even if we already synced this session.
        sessionStorage.removeItem(SESSION_SYNC_KEY);
      }
    } catch (e) {
      console.warn('[RM Referral] Could not read/store klaviyo_ref param(s)', e);
    }
  }

  /* ── Read back still-valid stored ref/source (or null if missing/expired) ── */
  function getStoredRef() {
    try {
      var id = localStorage.getItem(STORAGE_KEY_ID);
      var ts = localStorage.getItem(STORAGE_KEY_TS);
      if (!id || !ts) return null;
      var age = Date.now() - parseInt(ts, 10);
      if (age > MAX_AGE_MS) {
        localStorage.removeItem(STORAGE_KEY_ID);
        localStorage.removeItem(STORAGE_KEY_SOURCE);
        localStorage.removeItem(STORAGE_KEY_TS);
        return null;
      }
      return {
        id:     id,
        source: localStorage.getItem(STORAGE_KEY_SOURCE) || null,
      };
    } catch (e) {
      return null;
    }
  }

  /* ── Step 2: write klaviyo_ref (+ source) onto the cart as attributes (merge, don't clobber) ── */
  function syncRefToCart(ref) {
    fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var existing = (cart && cart.attributes) || {};
        var alreadyCurrent =
          existing.klaviyo_ref === ref.id &&
          (existing.klaviyo_ref_source || null) === (ref.source || null);

        if (alreadyCurrent) {
          try { sessionStorage.setItem(SESSION_SYNC_KEY, '1'); } catch (e) {}
          return;
        }

        var mergedAttributes = Object.assign({}, existing, {
          klaviyo_ref: ref.id,
          klaviyo_ref_source: ref.source || '',
        });

        return fetch('/cart/update.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ attributes: mergedAttributes }),
        })
          .then(function (res) { return res.json(); })
          .then(function () {
            try { sessionStorage.setItem(SESSION_SYNC_KEY, '1'); } catch (e) {}
          });
      })
      .catch(function (err) {
        console.warn('[RM Referral] Cart sync failed', err);
      });
  }

  /* ── Init ── */
  captureRefFromUrl();

  var storedRef = getStoredRef();
  if (storedRef) {
    var alreadySynced = false;
    try { alreadySynced = sessionStorage.getItem(SESSION_SYNC_KEY) === '1'; } catch (e) {}
    if (!alreadySynced) {
      syncRefToCart(storedRef);
    }
  }

})();
